import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Heart, Loader2, Shield, ArrowLeft, Smartphone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { ForcedTwoFASetup } from './ForcedTwoFASetup';
import { twoFAApi, loginPageApi } from '../../lib/api';
import { getBackendUrl } from '../../lib/api';

// Fallback hero image when no custom upload is configured.
const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=1600&q=80';

const DEFAULT_TEXTS = {
    brand_title: 'BGPR-Akademie',
    brand_subtitle: 'Fort- & Weiterbildungen',
    section_heading: 'Anmelden',
    hero_headline: 'BGPR-Akademie',
    hero_caption_top: 'Fort- & Weiterbildungen',
    hero_caption_main: 'Berliner Gesellschaft für Prävention und Rehabilitation von Herzkreislauferkrankungen e. V.',
    footer_text: '© {year} Berliner Gesellschaft für Prävention und Rehabilitation von Herzkreislauferkrankungen e. V.',
    secondary_link_label: 'Sie sind Kursteilnehmer/in?',
    secondary_link_text: 'Zum Teilnehmer-Portal',
};

/**
 * Render the brand title with the part after the first "-" highlighted in primary.
 * Example: "BGPR-Akademie" → "BGPR" + accent("-Akademie").
 */
function renderBrand(title, accentClass = 'text-primary') {
    if (!title) return null;
    const idx = title.indexOf('-');
    if (idx <= 0) return <span>{title}</span>;
    return (
        <>
            {title.slice(0, idx)}
            <span className={accentClass}>{title.slice(idx)}</span>
        </>
    );
}

/**
 * Split-screen authentication shell.
 * Left: white surface with the form. Right (lg+): branded hero image.
 * Mobile: form-only (hero hidden).
 */
function AuthShell({ children, subtitle, config }) {
    const cfg = { ...DEFAULT_TEXTS, ...(config || {}) };
    const heroSrc = config?.hero_image_url
        ? `${getBackendUrl()}${config.hero_image_url}`
        : DEFAULT_HERO_IMAGE;
    const year = new Date().getFullYear();
    const footer = (cfg.footer_text || '').replace('{year}', year);
    return (
        <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] bg-white">
            {/* LEFT — form column */}
            <div className="flex flex-col justify-between px-6 py-10 sm:px-12 lg:px-16 xl:px-24">
                {/* Top — logo / wordmark */}
                <div className="flex items-center gap-3" data-testid="login-brand">
                    <div className="p-2.5 rounded-xl bg-primary shadow-sm">
                        <Heart className="h-6 w-6 text-white" fill="currentColor" />
                    </div>
                    <div className="leading-tight">
                        <div className="text-2xl font-bold tracking-tight text-gray-900">
                            {renderBrand(cfg.brand_title)}
                        </div>
                        <div className="text-[11px] tracking-[0.18em] uppercase text-gray-500 mt-0.5">
                            {subtitle || cfg.brand_subtitle}
                        </div>
                    </div>
                </div>

                {/* Middle — form */}
                <div className="w-full max-w-md mx-auto lg:mx-0 my-12 lg:my-0 flex-1 flex flex-col justify-center">
                    {children}
                </div>

                {/* Bottom — footer */}
                <div className="text-center text-xs text-gray-400" data-testid="login-footer">
                    {footer}
                </div>
            </div>

            {/* RIGHT — hero column (lg+ only) */}
            <div
                className="hidden lg:block relative overflow-hidden"
                style={{
                    backgroundImage: `url("${heroSrc}")`,
                    backgroundSize: 'cover',
                    // Anchor to the right side so motifs that sit in the right
                    // part of the photo (e.g. heart + stethoscope on the BGPR
                    // login image) stay visible. The left side of the photo,
                    // which is typically the calmer "negative space" area, is
                    // where we place the text block.
                    backgroundPosition: 'right center',
                }}
                data-testid="login-hero"
            >
                {/* Asymmetric scrim — darker on the LEFT (where the text sits)
                    and almost transparent on the RIGHT (where the motif sits).
                    Combined with the textShadow on the brand container this
                    keeps the headline readable while the photo subject remains
                    fully visible. */}
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: 'linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.45) 35%, rgba(0,0,0,0.15) 70%, rgba(0,0,0,0) 100%)',
                    }}
                    data-testid="login-hero-tint"
                />
                {/* Soft ellipse to deepen the text band even further. */}
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: 'radial-gradient(ellipse 55% 60% at 30% 50%, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 100%)',
                    }}
                    data-testid="login-hero-overlay"
                />

                {/* Branding — left-aligned in the column so the text sits over
                    the dark/empty area while the photo motif on the right
                    stays prominent. */}
                <div className="relative h-full flex flex-col items-start justify-center pl-12 pr-6 xl:pl-20 text-white" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.55)' }}>
                    <div className="flex flex-col items-start text-left max-w-md">
                        <div className="p-5 rounded-2xl bg-white/10 backdrop-blur-md ring-1 ring-white/20 mb-6">
                            <Heart className="h-12 w-12 text-white" fill="currentColor" />
                        </div>
                        <h1 className="text-5xl font-extrabold tracking-tight leading-none">
                            {renderBrand(cfg.hero_headline, 'text-white/90')}
                        </h1>
                        <div className="mt-3 h-[3px] w-20 rounded-full" style={{ backgroundColor: 'hsl(var(--primary))' }} />
                        {cfg.hero_caption_top && (
                            <p className="mt-6 text-sm tracking-[0.32em] uppercase text-white/90">
                                {cfg.hero_caption_top}
                            </p>
                        )}
                        {cfg.hero_caption_main && (
                            <p className="mt-8 text-base text-white/90 leading-relaxed">
                                {cfg.hero_caption_main}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Reusable "tab heading" styled like the matchball reference (uppercase text,
 * red underline accent). Pure visual element.
 */
function SectionHeading({ children }) {
    return (
        <div className="mb-8" data-testid="login-section-heading">
            <h2 className="text-base font-semibold tracking-[0.22em] uppercase text-gray-800">
                {children}
            </h2>
            <div className="mt-3 h-[3px] w-12 bg-primary rounded-full" />
            <div className="mt-3 h-px w-full bg-gray-200" />
        </div>
    );
}

export function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    // challenge = { challenge_id, method, methods, picked }
    //   methods: ['totp','email'] — vom Backend; picked: vom User gewählte Methode (nur wenn methods.length>1)
    const [challenge, setChallenge] = useState(null);
    const [forcedSetup, setForcedSetup] = useState(null);
    const [code, setCode] = useState('');
    const [rememberDevice, setRememberDevice] = useState(false);
    const [config, setConfig] = useState(null);
    const { login, verify2FA, completeForcedSetup } = useAuth();
    const navigate = useNavigate();

    // Texts + Hero-Bild aus den Admin-Settings laden (mit Default-Fallback).
    useEffect(() => {
        let mounted = true;
        loginPageApi.getPublic()
            .then((res) => { if (mounted) setConfig(res.data || {}); })
            .catch(() => { if (mounted) setConfig({}); });
        return () => { mounted = false; };
    }, []);

    // Zuletzt erfolgreich verwendete 2FA-Methode (pro Gerät via localStorage).
    // Wird beim Multi-Methoden-Picker als Default hervorgehoben.
    const lastUsedMethod = (() => {
        try { return localStorage.getItem('bgpr_last_2fa_method'); } catch { return null; }
    })();

    const goAfterLogin = (user) => {
        if (user.role === 'admin' || user.role === 'super_admin') navigate('/admin');
        else navigate('/portal');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const result = await login(email, password);
            if (result?.requires_2fa_setup) {
                setForcedSetup({
                    setup_id: result.setup_id,
                    user_email: result.user_email,
                    user_name: result.user_name,
                });
                toast.info('Bitte richten Sie jetzt Ihre Zwei-Faktor-Authentifizierung ein.');
                return;
            }
            if (result?.requires_2fa) {
                const methods = result.methods || (result.method ? [result.method] : []);
                const needsPicker = methods.length > 1;
                setChallenge({
                    challenge_id: result.challenge_id,
                    method: result.method,
                    methods,
                    picked: needsPicker ? null : (methods[0] || result.method),
                });
                if (!needsPicker && (methods[0] || result.method) === 'email') {
                    toast.info('Wir haben Ihnen einen Code per E-Mail geschickt.');
                }
                return;
            }
            toast.success('Erfolgreich angemeldet');
            goAfterLogin(result);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
        } finally {
            setLoading(false);
        }
    };

    const pickMethod = async (method) => {
        if (!challenge) return;
        if (method === 'email') {
            setLoading(true);
            try {
                await twoFAApi.requestEmailOtpForChallenge(challenge.challenge_id);
                toast.info('Wir haben Ihnen einen Code per E-Mail geschickt.');
                setChallenge({ ...challenge, picked: 'email' });
            } catch (e) {
                toast.error(e.response?.data?.detail || 'E-Mail konnte nicht versendet werden');
            } finally { setLoading(false); }
        } else {
            setChallenge({ ...challenge, picked: 'totp' });
        }
    };

    const handle2FA = async (e) => {
        e.preventDefault();
        if (!code.trim()) return;
        setLoading(true);
        try {
            const user = await verify2FA(challenge.challenge_id, code.trim(), rememberDevice);
            // Merken, welche Methode der User dieses Mal verwendet hat —
            // beim nächsten Multi-Methoden-Login als Default vorauswählen.
            const usedMethod = challenge.picked || challenge.method;
            if (usedMethod === 'totp' || usedMethod === 'email') {
                try { localStorage.setItem('bgpr_last_2fa_method', usedMethod); } catch { /* */ }
            }
            toast.success(rememberDevice
                ? 'Erfolgreich angemeldet — dieses Gerät wird 30 Tage gemerkt.'
                : 'Erfolgreich angemeldet');
            goAfterLogin(user);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Code ungültig');
        } finally {
            setLoading(false);
        }
    };

    // Soll der Picker statt das Code-Formular gezeigt werden?
    const showPicker = !!(challenge && (challenge.methods?.length || 0) > 1 && !challenge.picked);
    const activeMethod = challenge?.picked || challenge?.method;

    if (forcedSetup) {
        return (
            <ForcedTwoFASetup
                session={forcedSetup}
                onCompleted={async (finalLogin) => {
                    if (finalLogin?.token && finalLogin?.user) {
                        await completeForcedSetup(finalLogin.token, finalLogin.user);
                        toast.success('Zwei-Faktor-Authentifizierung aktiviert — Sie sind angemeldet.');
                        goAfterLogin(finalLogin.user);
                    }
                    setForcedSetup(null);
                }}
                onCancel={() => setForcedSetup(null)}
            />
        );
    }

    // ---- 2FA: method picker ----
    if (showPicker) {
        return (
            <AuthShell subtitle="Zwei-Faktor-Authentifizierung" config={config}>
                <SectionHeading>Verifizieren</SectionHeading>
                <p className="text-sm text-gray-600 mb-6">
                    Bitte wählen Sie, wie Sie sich verifizieren möchten.
                </p>
                <div className="space-y-3" data-testid="login-2fa-method-picker">
                    {(() => {
                        const totpBtn = (
                            <button
                                key="totp"
                                type="button"
                                onClick={() => pickMethod('totp')}
                                disabled={loading}
                                data-testid="login-2fa-pick-totp"
                                className={`w-full text-left rounded-lg border p-4 hover:border-primary hover:bg-red-50/40 transition-colors flex items-start gap-3 disabled:opacity-50 ${lastUsedMethod === 'totp' ? 'border-primary bg-red-50/60' : 'border-gray-200'}`}
                            >
                                <div className="p-2 bg-primary/10 rounded-md">
                                    <Smartphone className="h-5 w-5 text-primary" />
                                </div>
                                <div className="flex-1">
                                    <div className="font-medium flex items-center gap-2">
                                        Authenticator-App
                                        {lastUsedMethod === 'totp' && (
                                            <span className="text-[10px] uppercase tracking-wider bg-primary/15 text-primary px-1.5 py-0.5 rounded">
                                                Zuletzt verwendet
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        Code aus Ihrer App eingeben (Google Authenticator, Authy, 1Password &hellip;).
                                    </div>
                                </div>
                            </button>
                        );
                        const emailBtn = (
                            <button
                                key="email"
                                type="button"
                                onClick={() => pickMethod('email')}
                                disabled={loading}
                                data-testid="login-2fa-pick-email"
                                className={`w-full text-left rounded-lg border p-4 hover:border-primary hover:bg-red-50/40 transition-colors flex items-start gap-3 disabled:opacity-50 ${lastUsedMethod === 'email' ? 'border-primary bg-red-50/60' : 'border-gray-200'}`}
                            >
                                <div className="p-2 bg-primary/10 rounded-md">
                                    <Mail className="h-5 w-5 text-primary" />
                                </div>
                                <div className="flex-1">
                                    <div className="font-medium flex items-center gap-2">
                                        Code per E-Mail anfordern
                                        {lastUsedMethod === 'email' && (
                                            <span className="text-[10px] uppercase tracking-wider bg-primary/15 text-primary px-1.5 py-0.5 rounded">
                                                Zuletzt verwendet
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        Wir senden Ihnen einen 6-stelligen Einmal-Code an Ihre hinterlegte
                                        E-Mail-Adresse.
                                    </div>
                                </div>
                            </button>
                        );
                        return lastUsedMethod === 'email' ? [emailBtn, totpBtn] : [totpBtn, emailBtn];
                    })()}
                    {loading && (
                        <div className="flex justify-center pt-2">
                            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                        </div>
                    )}
                    <Button type="button" variant="ghost" className="w-full" onClick={() => { setChallenge(null); setCode(''); }} data-testid="login-2fa-picker-back">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Zurück zum Login
                    </Button>
                </div>
            </AuthShell>
        );
    }

    // ---- 2FA: code form ----
    if (challenge) {
        return (
            <AuthShell subtitle="Zwei-Faktor-Authentifizierung" config={config}>
                <SectionHeading>Code eingeben</SectionHeading>
                <p className="text-sm text-gray-600 mb-6">
                    {activeMethod === 'email'
                        ? 'Bitte den 6-stelligen Code aus Ihrer E-Mail eingeben.'
                        : 'Bitte den 6-stelligen Code aus Ihrer Authenticator-App eingeben.'}
                </p>
                <form onSubmit={handle2FA} className="space-y-5" data-testid="login-2fa-form">
                    <div className="space-y-2">
                        <Label htmlFor="code" className="text-xs tracking-[0.18em] uppercase text-gray-700">Code (oder Backup-Code)</Label>
                        <Input
                            id="code"
                            type="text"
                            inputMode="text"
                            autoComplete="one-time-code"
                            placeholder="123456"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            required
                            autoFocus
                            className="h-12 text-center font-mono tracking-[0.4em] text-lg"
                            data-testid="login-2fa-code"
                        />
                        <p className="text-xs text-gray-500">
                            Sollten Sie keinen Zugriff auf den 2.&nbsp;Faktor haben, geben Sie einen
                            Backup-Code (z.&nbsp;B. <span className="font-mono">XXXX-XXXX-XXXX</span>) ein.
                        </p>
                    </div>
                    <label className="flex items-start gap-2.5 text-sm cursor-pointer select-none p-2 -ml-2 rounded-md hover:bg-gray-50 transition-colors" data-testid="remember-device-label">
                        <input
                            type="checkbox"
                            checked={rememberDevice}
                            onChange={(e) => setRememberDevice(e.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            data-testid="remember-device-checkbox"
                        />
                        <span>
                            <span className="font-medium">Diesen Computer 30 Tage merken</span>
                            <span className="block text-xs text-gray-500">
                                Nur auf privaten, vertrauenswürdigen Geräten aktivieren — die 2FA-Abfrage entfällt dann für künftige Anmeldungen.
                            </span>
                        </span>
                    </label>
                    <Button type="submit" className="w-full h-12 bg-primary hover:bg-primary/90 tracking-[0.18em] uppercase text-sm font-semibold" disabled={loading || !code.trim()} data-testid="login-2fa-submit">
                        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Shield className="mr-2 h-4 w-4" />}
                        Bestätigen
                    </Button>
                    <Button type="button" variant="ghost" className="w-full" onClick={() => {
                        // Wenn der User zwischen 2 Methoden wählen konnte, zurück zum Picker.
                        if ((challenge.methods?.length || 0) > 1) {
                            setChallenge({ ...challenge, picked: null });
                            setCode('');
                        } else {
                            setChallenge(null);
                            setCode('');
                        }
                    }} data-testid="login-2fa-back">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Zurück
                    </Button>
                </form>
            </AuthShell>
        );
    }

    // ---- Standard login ----
    const cfg = { ...DEFAULT_TEXTS, ...(config || {}) };
    return (
        <AuthShell config={config}>
            <SectionHeading>{cfg.section_heading}</SectionHeading>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs tracking-[0.18em] uppercase text-gray-700">E-Mail</Label>
                    <Input
                        id="email"
                        type="email"
                        placeholder="ihre@email.de"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                        className="h-12"
                        data-testid="login-email-input"
                    />
                </div>
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label htmlFor="password" className="text-xs tracking-[0.18em] uppercase text-gray-700">Passwort</Label>
                        <Link
                            to="/passwort-zuruecksetzen"
                            className="text-xs text-gray-500 hover:text-primary transition-colors"
                            data-testid="login-forgot-password-link"
                        >
                            Passwort vergessen?
                        </Link>
                    </div>
                    <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        className="h-12"
                        data-testid="login-password-input"
                    />
                </div>
                <Button
                    type="submit"
                    className="w-full h-12 bg-primary hover:bg-primary/90 tracking-[0.18em] uppercase text-sm font-semibold"
                    disabled={loading}
                    data-testid="login-submit-button"
                >
                    {loading ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird angemeldet&hellip;</>
                    ) : 'Anmelden'}
                </Button>
            </form>

            {/* Sekundärer Pfad: Teilnehmer-Login (öffentlicher Bereich) */}
            <div className="mt-8 text-center text-sm text-gray-500">
                {cfg.secondary_link_label}{' '}
                <Link
                    to="/teilnehmer/login"
                    className="font-semibold text-primary hover:underline"
                    data-testid="login-participant-link"
                >
                    {cfg.secondary_link_text}
                </Link>
            </div>
        </AuthShell>
    );
}
